import React, { useEffect, useMemo, useState } from 'react';
import styles from './PreviewPanel.module.scss';
import cn from 'classnames';
import HeaderPreview from '../HeaderPreview/HeaderPreview';
import mammoth from 'mammoth';
import useAttachedFileQuery from '../../queries/useAttachedFileQuery';
import { Spin } from 'antd';
import { ALLOWED_DOC_ATTACHMENT_FILE_FORMATS } from '../../constants/allowedDocAttachmentFileFormats';

const getMimeType = (extension) => {
  switch (extension?.toLowerCase()) {
    case 'pdf':
      return 'application/pdf';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'doc':
      return 'application/msword';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'tif':
    case 'tiff':
      return 'image/tiff';
    case 'webp':
      return 'image/webp';
    default:
      return '';
  }
};

const DocxPreview = ({ file }) => {
  const [docxHtml, setDocxHtml] = useState('');

  useEffect(() => {
    if (!file) return;
    if (
      file.type ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const arrayBuffer = e.target.result;
        try {
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setDocxHtml(result.value);
        } catch (err) {
          console.error('Ошибка при конвертации DOCX:', err);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }, [file]);

  return (
    <div
      style={{
        border: '1px solid #ccc',
        padding: 10,
        overflow: 'auto',
        maxWidth: '100%',
        maxHeight: '100%',
        height: 'auto',
      }}
      dangerouslySetInnerHTML={{ __html: docxHtml }}
    />
  );
};

const FilePreview = ({ uploadFile = {} }) => {
  const { data, isFetching } = useAttachedFileQuery(
    {
      docId: uploadFile?.docId,
      fileName: uploadFile?.fileName,
    },
    {
      enabled: !!uploadFile?.id && !uploadFile?.originFileObj,
      staleTime: Infinity,
    }
  );

  const { blob, fileType, fileName } = useMemo(() => {
    if (uploadFile?.originFileObj) {
      return {
        blob: uploadFile.originFileObj,
        fileType: uploadFile.originFileObj.type,
        fileName: uploadFile.originFileObj.name,
      };
    }

    if (data) {
      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(null)
        .map((_, i) => byteCharacters.charCodeAt(i));

      const byteArray = new Uint8Array(byteNumbers);

      const type = getMimeType(uploadFile?.extension);

      return {
        blob: new Blob([byteArray], { type }),
        fileType: type,
        fileName: uploadFile?.fileName,
      };
    }

    return {};
  }, [
    data,
    uploadFile.fileName,
    uploadFile.originFileObj,
    uploadFile.extension,
  ]);

  const url = useMemo(() => {
    if (!blob) return null;
    return URL.createObjectURL(blob);
  }, [blob]);

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  if (isFetching) {
    return (
      <div className={styles.textInfo}>
        <Spin />
      </div>
    );
  }

  if (!blob || !fileType)
    return (
      <div className={styles.textInfo}>
        Не поддерживаемый формат для предпросмотра файла
      </div>
    );

  const IMAGE_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/tiff',
    'image/webp',
  ];
  const imageExtension = fileType.split('/')[1];
  const isAllowedImage =
    IMAGE_MIME_TYPES.includes(fileType) &&
    ALLOWED_DOC_ATTACHMENT_FILE_FORMATS.includes(imageExtension);

  if (isAllowedImage) {
    return (
      <img
        src={url}
        alt={fileName}
        style={{ maxWidth: '100%', maxHeight: '100%', height: 'auto' }}
      />
    );
  }

  if (fileType === 'application/pdf' || fileType === 'pdf') {
    return (
      <iframe
        src={url}
        title={fileName}
        width="100%"
        height="100%"
      />
    );
  }

  if (
    fileType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    fileType === 'docx'
  ) {
    return <DocxPreview file={blob} />;
  }

  return (
    <div className={styles.textInfo}>
      Не поддерживаемый формат для предпросмотра файла
    </div>
  );
};

const PreviewPanel = ({ files = [], valueVisible = 0, onChangePreview }) => {
  const handleChangeFilesOrder = (newValue) => {
    onChangePreview({ visible: true, value: newValue });
  };

  return (
    <div className={styles.previewPanel}>
      <div className={styles.content}>
        <HeaderPreview
          filesOrder={valueVisible}
          onChangeFilesOrder={handleChangeFilesOrder}
          files={files}
        />
        <div
          className={cn({
            [styles.preview]: !files[valueVisible]?.type?.startsWith('image/'),
            [styles.previewImage]:
              files[valueVisible]?.type?.startsWith('image/'),
          })}
        >
          <FilePreview uploadFile={files[valueVisible]} />
        </div>
      </div>
    </div>
  );
};

export default PreviewPanel;
